import { useEffect, useMemo, useState } from "react";
import { showAlert } from "../utils/Helper";

const MAX_DOC_KB = 200;

function compressImage(imageFile, maxKB) {
  return new Promise((resolve) => {
    const maxBytes = maxKB * 1024;
    if (imageFile.size <= maxBytes) { resolve(imageFile); return; }
    const img = new Image();
    const url = URL.createObjectURL(imageFile);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      let quality = 0.85;
      const tryNext = () => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(imageFile); return; }
          if (blob.size <= maxBytes || quality <= 0.1) {
            resolve(new File([blob], imageFile.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
          } else {
            quality = Math.max(0.1, quality - 0.1);
            tryNext();
          }
        }, "image/jpeg", quality);
      };
      tryNext();
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(imageFile); };
    img.src = url;
  });
}

function toTitleCase(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trimStart()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Derives Valid Upto (Date of Retirement) from DOB + Status.
// W: retires at 60 (must be under 60 already). E: Extended Service, age 60-64,
// renews +1 year from today. R/H: Retired/Emeritus, age 60+; retires at 70 if
// under 70, else +5 years from today. Returns { dor, error } — error is set
// (and dor left null) when the DOB/Status combination is invalid for that status.
function calculateDOR(dobStr, status) {
  if (!dobStr) return { dor: null, error: null };
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return { dor: null, error: null };

  const today = new Date();
  const dobDay = dob.getDate();
  const dobMonth = dob.getMonth();
  const dobYear = dob.getFullYear();

  let age = today.getFullYear() - dobYear;
  const thisYearBirthday = new Date(today.getFullYear(), dobMonth, dobDay);
  if (today < thisYearBirthday) age--;

  let dorDate = null;

  if (status === "W") {
    if (age >= 60) {
      return { dor: null, error: "Employee age is 60 or above. Please select 'Extended Service' or 'Retiree'." };
    }
    const retirementYear = dobYear + 60;
    dorDate = dobDay === 1 ? new Date(retirementYear, dobMonth, 0) : new Date(retirementYear, dobMonth + 1, 0);
  } else if (status === "E") {
    if (age < 60) {
      return { dor: null, error: "Extended Service is allowed only for age 60 and above." };
    }
    if (age >= 65) {
      return { dor: null, error: "Extended Service not allowed beyond age 65." };
    }
    dorDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
  } else if (status === "R" || status === "H") {
    if (age < 60) {
      return { dor: null, error: "Retiree/Emeritus status is allowed only for age 60 and above." };
    }
    if (age < 70) {
      const retirementYear = dobYear + 70;
      dorDate = dobDay === 1 ? new Date(retirementYear, dobMonth, 0) : new Date(retirementYear, dobMonth + 1, 0);
    } else {
      dorDate = new Date(today.getFullYear() + 5, today.getMonth(), today.getDate());
    }
  }

  if (!dorDate) return { dor: null, error: null };

  const yyyy = dorDate.getFullYear();
  const mm = String(dorDate.getMonth() + 1).padStart(2, "0");
  const dd = String(dorDate.getDate()).padStart(2, "0");
  return { dor: `${yyyy}-${mm}-${dd}`, error: null };
}

const EMPTY_FORM = {
  NameTitleID: "",
  EmpName: "",
  FNameTitleID: "",
  FatherName: "",
  Gender: "M",
  Country: "91",
  Mobile: "",
  EmgMobile: "",
  DOB: "",
  DOR: "",
  EMail: "",
  PermanentAddress: "",
  PermanentStateID: "",
  PermanentCityID: "",
  PermanentPIN: "",
  DesignationID: "",
  DepartmentID: "",
  PFNo: "",
  EmpType: "T",
  JobType: "P",
  BloodGroupID: "",
  Status: "W"
};

export default function EmployeeRegister() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dobParts, setDobParts] = useState({ day: "", month: "", year: "" });
  const [dorWarning, setDorWarning] = useState("");

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [titles, setTitles] = useState([]);
  const [ftitles, setFTitles] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [bloodGroups, setBloodGroups] = useState([]);

  useEffect(() => {
    fetch("/api/states").then(r => r.json()).then(d => setStates(Array.isArray(d) ? d : [])).catch(() => setStates([]));
    fetch("/api/meta/name-titles").then(r => r.json()).then(d => {
      const all = Array.isArray(d) ? d : [];
      const EXCLUDED_TITLE_IDS = new Set([7, 15, 16, 17, 18, 20]);
      setTitles(all.filter(t => !EXCLUDED_TITLE_IDS.has(Number(t.NameTitleID))));
      setFTitles(all);
    }).catch(() => { setTitles([]); setFTitles([]); });
    fetch("/api/meta/designations").then(r => r.json()).then(d => setDesignations(Array.isArray(d) ? d : [])).catch(() => setDesignations([]));
    const EXCLUDED_DEPT_IDS = new Set([500, 501]);
    fetch("/api/meta/departments").then(r => r.json()).then(d => setDepartments((Array.isArray(d) ? d : []).filter(dept => !EXCLUDED_DEPT_IDS.has(Number(dept.DepartmentID))))).catch(() => setDepartments([]));
    fetch("/api/meta/bloodgroups").then(r => r.json()).then(d => setBloodGroups(Array.isArray(d) ? d : [])).catch(() => setBloodGroups([]));
  }, []);

  useEffect(() => {
    if (!form.PermanentStateID) {
      setCities([]);
      setForm(prev => ({ ...prev, PermanentCityID: "" }));
      return;
    }
    fetch(`/api/cities?stateId=${form.PermanentStateID}`)
      .then(r => r.json())
      .then(d => setCities(Array.isArray(d) ? d : []))
      .catch(() => setCities([]));
  }, [form.PermanentStateID]);

  const days   = useMemo(() => [...Array(31)].map((_, i) => i + 1), []);
  const months = useMemo(() => [...Array(12)].map((_, i) => i + 1), []);
  const years  = useMemo(() => [...Array(80)].map((_, i) => new Date().getFullYear() - i), []);

  const handleDOBChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...dobParts, [name]: value };
    setDobParts(updated);
    if (updated.day && updated.month && updated.year) {
      const dob = `${updated.year}-${updated.month}-${updated.day}`;
      const { dor, error } = calculateDOR(dob, form.Status);
      setDorWarning(error || "");
      setForm(prev => ({ ...prev, DOB: dob, DOR: dor || prev.DOR }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (["Mobile", "EmgMobile", "PermanentPIN"].includes(name)) {
      if (!/^\d*$/.test(value)) return;
      setForm(prev => ({ ...prev, [name]: value }));
      return;
    }

    if (["EmpName", "FatherName"].includes(name)) {
      const filtered = value.replace(/[^a-zA-Z\s.]/g, "");
      setForm(prev => ({ ...prev, [name]: toTitleCase(filtered) }));
      return;
    }

    if (name === "EMail") {
      setForm(prev => ({ ...prev, EMail: value.toLowerCase().trim() }));
      return;
    }

    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const required = [
      "NameTitleID", "EmpName", "FNameTitleID", "FatherName",
      "Gender", "Country", "Mobile", "EmgMobile", "DOB",
      "EMail", "PermanentAddress", "PermanentStateID", "PermanentCityID",
      "PermanentPIN", "DesignationID", "DepartmentID", "PFNo", "BloodGroupID"
    ];
    for (const f of required) {
      if (!String(form[f] || "").trim()) {
        showAlert("All mandatory fields must be filled", null, "error");
        return;
      }
    }

    if (!/^\d{1,4}$/.test(String(form.Country || "").trim())) {
      showAlert("Country code must be 1 to 4 digits", null, "error");
      return;
    }

    const mobile = String(form.Mobile || "").replace(/^0+/, "");
    if (!/^\d{10}$/.test(mobile)) {
      showAlert("Mobile must be exactly 10 digits", null, "error");
      return;
    }
    if (/^(\d)\1{9}$/.test(mobile)) {
      showAlert("Please enter a valid mobile number", null, "error");
      return;
    }

    const emg = String(form.EmgMobile || "").replace(/^0+/, "");
    if (!/^\d{10}$/.test(emg)) {
      showAlert("Emergency Mobile must be exactly 10 digits", null, "error");
      return;
    }
    if (/^(\d)\1{9}$/.test(emg)) {
      showAlert("Please enter a valid emergency mobile number", null, "error");
      return;
    }
    if (mobile === emg) {
      showAlert("Emergency mobile must be different from primary mobile", null, "error");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.EMail || "")) {
      showAlert("Invalid email format", null, "error");
      return;
    }

    if (String(form.EmpName || "").replace(/[^a-zA-Z]/g, "").length < 2) {
      showAlert("Employee Name must contain at least 2 letters", null, "error");
      return;
    }

    if (String(form.FatherName || "").replace(/[^a-zA-Z]/g, "").length < 2) {
      showAlert("Father Name must contain at least 2 letters", null, "error");
      return;
    }

    if (!/^\d{6}$/.test(form.PermanentPIN || "")) {
      showAlert("PIN must be exactly 6 digits", null, "error");
      return;
    }

    if (!["M", "F", "O"].includes(String(form.Gender || "").toUpperCase())) {
      showAlert("Invalid gender value", null, "error");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dob = new Date(form.DOB);
    if (Number.isNaN(dob.getTime())) {
      showAlert("Invalid date of birth", null, "error");
      return;
    }
    const minDob = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
    if (dob > minDob) {
      showAlert("Age must be at least 18 years", null, "error");
      return;
    }

    if (!file) {
      showAlert("Please upload official ID proof", null, "error");
      return;
    }

    const allowed = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) {
      showAlert("Only JPG, JPEG, PNG files are allowed", null, "error");
      return;
    }

    let uploadFile = file;
    if (file.type.startsWith("image/") && file.size > MAX_DOC_KB * 1024) {
      uploadFile = await compressImage(file, MAX_DOC_KB);
    }
    if (uploadFile.size > MAX_DOC_KB * 1024) {
      showAlert(`Supporting document must be less than ${MAX_DOC_KB} KB`, null, "error");
      return;
    }

    const cleanForm = { ...form, Mobile: mobile, EmgMobile: emg };
    const fd = new FormData();
    Object.keys(cleanForm).forEach(k => fd.append(k, cleanForm[k]));
    fd.append("SupportingDoc", uploadFile);

    try {
      setSaving(true);
      const res = await fetch("/api/employees/register", { method: "POST", body: fd });
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server error. Please contact administrator.");
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      showAlert(data.message, "/dashboard", "success");
      setForm(EMPTY_FORM);
      setDobParts({ day: "", month: "", year: "" });
      setFile(null);
    } catch (err) {
      showAlert(err.message || "Registration failed", null, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="container-fluid" style={{ textAlign: "left", marginTop: "10px" }}>
      <h3 className="ctitle">PU Employee Registration</h3>
      <div className="col-lg-10">
        <form onSubmit={handleSubmit} encType="multipart/form-data">

          {/* Row 1: Title / Employee Name / Gender / Father Title / Father Name */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-12 col-sm-1">
              <label className="form-label mb-1">Title</label>
              <select className="form-select" name="NameTitleID" value={form.NameTitleID} onChange={handleChange} required>
                <option value="">Select</option>
                {titles.map(t => <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>)}
              </select>
            </div>

            <div className="col-12 col-sm-3">
              <label className="form-label mb-1">Employee Name</label>
              <input className="form-control" name="EmpName" value={form.EmpName} onChange={handleChange} placeholder="Employee Name...*" required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Gender</label>
              <select className="form-select" name="Gender" value={form.Gender} onChange={handleChange} required>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Others</option>
              </select>
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Father Title</label>
              <select className="form-select" name="FNameTitleID" value={form.FNameTitleID} onChange={handleChange} required>
                <option value="">Select</option>
                {ftitles.map(t => <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>)}
              </select>
            </div>

            <div className="col-12 col-sm-4">
              <label className="form-label mb-1">Father Name</label>
              <input className="form-control" name="FatherName" value={form.FatherName} onChange={handleChange} placeholder="Father Name...*" required />
            </div>
          </div>

          {/* Row 2: Country / Mobile / Emg Mobile / DOB / Valid Upto */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-12 col-sm-1">
              <label className="form-label mb-1">Code</label>
              <input className="form-control" name="Country" value={form.Country} onChange={handleChange} required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Mobile (10 digits)*</label>
              <input
                className="form-control"
                name="Mobile"
                value={form.Mobile}
                onChange={handleChange}
                maxLength={10}
                inputMode="numeric"
                pattern="\d{10}"
                title="Enter exactly 10 digits"
                required
              />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Emergency Mobile*</label>
              <input
                className="form-control"
                name="EmgMobile"
                value={form.EmgMobile}
                onChange={handleChange}
                maxLength={10}
                inputMode="numeric"
                pattern="\d{10}"
                title="Enter exactly 10 digits"
                required
              />
            </div>

            <div className="col-12 col-sm-4">
              <label className="form-label mb-1">Date of Birth</label>
              <div className="d-flex gap-2">
                <select className="form-select" name="day" value={dobParts.day} onChange={handleDOBChange} required>
                  <option value="">DD</option>
                  {days.map(d => <option key={d} value={String(d).padStart(2, "0")}>{d}</option>)}
                </select>
                <select className="form-select" name="month" value={dobParts.month} onChange={handleDOBChange} required>
                  <option value="">MM</option>
                  {months.map(m => <option key={m} value={String(m).padStart(2, "0")}>{m}</option>)}
                </select>
                <select className="form-select" name="year" value={dobParts.year} onChange={handleDOBChange} required>
                  <option value="">YYYY</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            <div className="col-12 col-sm-3">
              <label className="form-label mb-1">Valid Upto (Date of Retirement)</label>
              <input type="date" className="form-control" name="DOR" value={form.DOR || ""} readOnly />
              {dorWarning && <div className="text-danger small mt-1">{dorWarning}</div>}
            </div>
          </div>

          {/* Row 3: Email / Blood Group / PF No / Status / Emp Type / Job Type */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-12 col-sm-4">
              <label className="form-label mb-1">Email</label>
              <input className="form-control" type="email" name="EMail" value={form.EMail} onChange={handleChange} placeholder="Email...*" required />
            </div>

            <div className="col-12 col-sm-1">
              <label className="form-label mb-1">BloodGrp</label>
              <select className="form-select" name="BloodGroupID" value={form.BloodGroupID} onChange={handleChange} required>
                <option value="">Select</option>
                {bloodGroups.map(b => <option key={b.BloodGroupID} value={b.BloodGroupID}>{b.BloodGroup}</option>)}
              </select>
            </div>

            <div className="col-12 col-sm-1">
              <label className="form-label mb-1">PF No.</label>
              <input className="form-control" name="PFNo" value={form.PFNo} onChange={handleChange} placeholder="PF No...*" required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Status</label>
              <select className="form-select" name="Status" value={form.Status} onChange={handleChange}>
                <option value="W">Working</option>
                <option value="E">Extended Service</option>
                <option value="R">Retired</option>
                <option value="H">Emeritus</option>
              </select>
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Emp Type</label>
              <select className="form-select" name="EmpType" value={form.EmpType} onChange={handleChange}>
                <option value="T">Teaching</option>
                <option value="N">Non-Teaching</option>
                <option value="A">Academic Staff</option>
              </select>
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Job Type</label>
              <select className="form-select" name="JobType" value={form.JobType} onChange={handleChange}>
                <option value="P">Permanent</option>
                <option value="C">Contractual</option>
                <option value="D">Daily Wages</option>
              </select>
            </div>
          </div>

          {/* Row 4: Permanent Address / PIN / State / City */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-12 col-sm-5">
              <label className="form-label mb-1">Permanent Address</label>
              <input className="form-control" name="PermanentAddress" value={form.PermanentAddress} onChange={handleChange} placeholder="Permanent Address...*" required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">PIN</label>
              <input className="form-control" name="PermanentPIN" value={form.PermanentPIN} onChange={handleChange} maxLength={6} inputMode="numeric" placeholder="PIN...*" required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">State</label>
              <select className="form-select" name="PermanentStateID" value={form.PermanentStateID} onChange={handleChange} required>
                <option value="">Select State</option>
                {states.map(s => <option key={s.StateID} value={s.StateID}>{s.State}</option>)}
              </select>
            </div>

            <div className="col-12 col-sm-3">
              <label className="form-label mb-1">City</label>
              <select className="form-select" name="PermanentCityID" value={form.PermanentCityID} onChange={handleChange} disabled={!form.PermanentStateID} required>
                <option value="">Select City</option>
                {cities.map(c => <option key={c.CityID} value={c.CityID}>{c.City}</option>)}
              </select>
            </div>
          </div>

          {/* Row 5: Designation / Department / Supporting Document */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-12 col-sm-4">
              <label className="form-label mb-1">Designation</label>
              <select className="form-select" name="DesignationID" value={form.DesignationID} onChange={handleChange} required>
                <option value="">Select Designation</option>
                {designations.map(d => <option key={d.DesignationID} value={d.DesignationID}>{d.Designation}</option>)}
              </select>
            </div>

            <div className="col-12 col-sm-4">
              <label className="form-label mb-1">Department</label>
              <select className="form-select" name="DepartmentID" value={form.DepartmentID} onChange={handleChange} required>
                <option value="">Select Department</option>
                {departments.map(d => <option key={d.DepartmentID} value={d.DepartmentID}>{d.Department}</option>)}
              </select>
            </div>

            <div className="col-12 col-sm-4">
              <label
                className="form-label mb-1"
                title={`Allowed: JPG, JPEG, PNG. Max ${MAX_DOC_KB}KB. Images above ${MAX_DOC_KB}KB are auto-compressed.`}
              >
                Supporting Doc (JPG/JPEG/PNG, &lt;{MAX_DOC_KB}KB)
                <i className="bi bi-info-circle ms-1 text-muted" style={{ fontSize: "0.85rem" }}></i>
              </label>
              <input
                type="file"
                className="form-control"
                accept=".jpg,.jpeg,.png"
                onChange={e => setFile(e.target.files?.[0] || null)}
                required
              />
            </div>
          </div>

          {/* Submit */}
          <div className="row">
            <div className="col-12">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? "Submitting..." : "Submit Registration"}
              </button>
            </div>
          </div>

        </form>
      </div>
    </section>
  );
}
